import { useRef, useState } from "react";
import { Upload, X, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

/**
 * Real image upload to object storage.
 * - multiple=false: value is a string URL, onChange(url)
 * - multiple=true:  value is an array of URLs, onChange(array)
 */
export default function ImageUpload({ value, onChange, multiple = false, label, testid = "image-upload" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const images = multiple ? (value || []) : value ? [value] : [];

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
        if (!multiple) break;
      }
      if (multiple) onChange([...(value || []), ...urls]);
      else onChange(urls[0]);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addByUrl = () => {
    const url = window.prompt("URL de la imagen");
    if (!url) return;
    if (multiple) onChange([...(value || []), url]);
    else onChange(url);
  };

  const remove = (i) => {
    if (multiple) onChange((value || []).filter((_, idx) => idx !== i));
    else onChange("");
  };

  return (
    <div data-testid={testid}>
      {label && <p className="overline text-muted-foreground mb-2">{label}</p>}
      <div className="flex flex-wrap gap-3">
        {images.map((img, i) => (
          <div key={i} className="relative w-20 h-24 bg-secondary overflow-hidden group border border-border">
            <img src={img} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => remove(i)} className="absolute top-0.5 right-0.5 h-5 w-5 bg-background/80 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testid}-remove-${i}`}>
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {(multiple || images.length === 0) && (
          <button type="button" onClick={() => fileRef.current?.click()} className="w-20 h-24 border border-dashed border-border grid place-items-center text-muted-foreground hover:border-gold hover:text-gold transition-colors" data-testid={`${testid}-btn`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple={multiple} hidden onChange={upload} />
      <button type="button" onClick={addByUrl} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-gold mt-2" data-testid={`${testid}-url`}>
        <Link2 className="h-3 w-3" /> o añadir por URL
      </button>
    </div>
  );
}
